import { useEffect } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { Menu } from "lucide-react";
import {
  Box,
  Drawer,
  DrawerContent,
  DrawerOverlay,
  Flex,
  Grid,
  Icon,
  IconButton,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { AppSidebar } from "../../../components/shell/AppSidebar";
import { BrandManager } from "../../../components/shell/BrandManager";
import { useAuth } from "../../../services/auth/AuthProvider";

export const CabinetLayout = () => {
  const { publishingHouse, isSuperadmin } = useAuth();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { pathname } = useLocation();

  // Close the mobile drawer on any navigation, including back/forward.
  useEffect(() => {
    onClose();
  }, [pathname, onClose]);

  const publishingHouseName = publishingHouse?.name ?? "";

  return (
    // `minmax(0, 1fr)` lets the main column shrink below its content width so
    // wide content (header controls, grid) never overflows to the right.
    // On mobile the sidebar column is dropped — the sidebar moves into a drawer.
    <Grid
      templateColumns={{ base: "minmax(0, 1fr)", md: "auto minmax(0, 1fr)" }}
      // `dvh` on mobile so the layout is not cut off by the browser chrome.
      h={{ base: "100dvh", md: "100vh" }}
      w="100%"
      maxW="100vw"
      overflow="hidden"
    >
      <Box display={{ base: "none", md: "block" }} h="full">
        <AppSidebar
          publishingHouseName={publishingHouseName}
          isSuperadmin={isSuperadmin}
        >
          <BrandManager />
        </AppSidebar>
      </Box>

      <Flex direction="column" minW={0} minH={0}>
        <Flex
          as="header"
          display={{ base: "flex", md: "none" }}
          align="center"
          gap={3}
          flexShrink={0}
          h="56px"
          px={2}
          bg="brand.charcoal"
          color="white"
        >
          <IconButton
            aria-label="Відкрити меню"
            icon={<Icon as={Menu} boxSize={5} />}
            onClick={onOpen}
            variant="ghost"
            color="white"
            _hover={{ bg: "whiteAlpha.200" }}
            _active={{ bg: "whiteAlpha.300" }}
          />
          <Text
            color="brand.orange"
            fontSize="md"
            fontWeight="medium"
            letterSpacing="0.2em"
            textTransform="uppercase"
            flexShrink={0}
          >
            Читанка
          </Text>
          <Text fontSize="sm" opacity={0.8} noOfLines={1}>
            {publishingHouseName}
          </Text>
        </Flex>

        <Box as="main" id="cabinet-main-content" flex={1} minW={0} minH={0} overflowY="auto" overflowX="hidden" bg="blackAlpha.50">
          <Outlet />
        </Box>
      </Flex>

      <Drawer isOpen={isOpen} onClose={onClose} placement="left">
        <DrawerOverlay />
        <DrawerContent maxW="64" bg="brand.charcoal">
          <AppSidebar
            publishingHouseName={publishingHouseName}
            isSuperadmin={isSuperadmin}
            onNavigate={onClose}
          >
            <BrandManager />
          </AppSidebar>
        </DrawerContent>
      </Drawer>
    </Grid>
  );
};
